import * as fs from 'fs';
import * as path from 'path';
import * as crypto from 'crypto';
import * as utils from '../utils';
import { debounce } from 'lodash';
import { getLogger } from './logger';

const logger = getLogger('local cache');

let cacheLocation = path.resolve(utils.getCliLocation(), 'testim-cache');

let encryptKeyResolve: undefined | ((value: string) => void);
let encryptAndSaveResolve: (value: { success: boolean; error?: any }) => void;
let cacheEnabled = true;
let cacheMissAllowed = true;
let waitingForSave = false;

let _encryptAndSavePromise = new Promise<{ success: boolean; error?: any }>(resolve => { encryptAndSaveResolve = resolve; });
const _encryptKeyPromise = new Promise<string>(resolve => { encryptKeyResolve = resolve; });

const THREE_HOURS = 1000 * 60 * 60 * 3;

export const getCacheFileLocation = () => path.resolve(path.resolve(cacheLocation, 'testim.cache'));

async function getLocalRunnerCache() {
    try {
        return await utils.promiseTimeout(
            fs.promises.readFile(getCacheFileLocation()).then(async buffer => {
                const key = await _encryptKeyPromise;
                return decrypt(key, buffer);
            }),
            30_000,
        );
    } catch {
        return {};
    }
}

let localRunnerCache = getLocalRunnerCache();

const encryptAndSave = debounce(async (object) => {
    let error;
    try {
        const key = await _encryptKeyPromise;
        const iv = crypto.randomBytes(16);
        const objStr = JSON.stringify(object);
        const keyBuffer = Buffer.from(key);
        const cipher = crypto.createCipheriv('aes-256-cbc', Buffer.concat([keyBuffer, Buffer.alloc(32)], 32), iv);
        const result = Buffer.concat([iv, cipher.update(objStr), cipher.final()]);
        const pathExists = await utils.doesPathExist(cacheLocation);
        if (!pathExists) {
            await fs.promises.mkdir(cacheLocation, { recursive: true });
        }
        await fs.promises.writeFile(getCacheFileLocation(), result);
    } catch (err) {
        logger.error('failed saving cache', { err });
        error = err;
    }
    if (error) {
        encryptAndSaveResolve({ success: false, error });
    } else {
        encryptAndSaveResolve({ success: true });
    }
}, 200);

function decrypt(key: string, buffer: Buffer) {
    const iv = buffer.slice(0, 16);
    const encryptedText = buffer.slice(16);
    const keyBuffer = Buffer.from(key);
    const decipher = crypto.createDecipheriv('aes-256-cbc', Buffer.concat([keyBuffer, Buffer.alloc(32)], 32), iv);
    const decrypted = decipher.update(encryptedText);
    // @ts-expect-error JSON.parse handles buffers as well, the types are not smart enough :(
    return JSON.parse(Buffer.concat([decrypted, decipher.final()]));
}

/** argument-less memoize for functions with a global cache name} */
export function memoize<T>(fn: () => PromiseLike<T>, fnName: string, duration = THREE_HOURS, parameters: any = undefined) {
    return async () => {
        if (!cacheEnabled) {
            return await fn();
        }
        const originalFnName = fnName;
        if (parameters) {
            fnName += JSON.stringify(parameters);
        }
        const cached = await get<T>(fnName);
        if (cached) {
            logger.debug('cache hit:', { fnName });
            return cached;
        }
        logger.debug('cache miss:', { fnName });
        if (!cacheMissAllowed) {
            throw new Error(`Attempted to rebuild cache for ${originalFnName}. cache miss is not allowed with current run configuration`);
        }
        const value = await fn();
        if (value) {
            await set(fnName, value, duration);
        }
        return value;
    };
}

export async function get<T = any>(key: string) {
    const obj = await localRunnerCache;
    const valueExpiry = obj[key];
    if (!valueExpiry) {
        return undefined; // not in cache
    }
    const { value, expiry } = valueExpiry;
    if (expiry < Date.now()) {
        return undefined;
    }
    if (!value) {
        return undefined;
    }
    return value as T;
}

export async function set(key: string, value: any, ttl: number) {
    if (waitingForSave) {
        logger.error('calling set after waitForSave is not allowed', { key, ttl });
        throw new Error('calling set after waitForSave is not allowed');
    }
    try {
        const obj = await localRunnerCache;
        obj[key] = { value, expiry: Date.now() + ttl };
        _encryptAndSavePromise = new Promise(resolve => { encryptAndSaveResolve = resolve; });
        encryptAndSave(obj);
    } catch (e) {
        logger.error('failed updating cache');
    }
}

export async function clear() {
    const obj = await localRunnerCache;
    Object.keys(obj).forEach(key => {
        delete obj[key];
    });
}

function setEnabled(enabled: boolean) {
    cacheEnabled = enabled;
}

function _enableCacheMiss(enabled = true) {
    cacheMissAllowed = enabled;
}

export async function waitForSave() {
    try {
        waitingForSave = true;
        return await _encryptAndSavePromise;
    } finally {
        waitingForSave = false;
    }
}

export function setCacheLocation(location: string) {
    cacheLocation = location;
    localRunnerCache = getLocalRunnerCache();
}

export const setEncryptKey = encryptKeyResolve;
export const disable = setEnabled.bind(this, false);
export const enable = setEnabled.bind(this, true);
export const disableCacheMiss = () => _enableCacheMiss(false);
export const enableCacheMiss = () => _enableCacheMiss(true);
export const isEnabled = function () { return cacheEnabled; };
